/**
 * Strips credentials and personal data from text before it leaves PixelSign
 * for an error tracker: signing, Q&A, invitation, reset, and confirmation link
 * tokens; API keys and webhook secrets; bearer and JWT tokens; and email
 * addresses. Used by both the API and the web app, so what one scrubs the
 * other does too.
 */

/** A path segment after one of the token routes (web and API). */
const LINK_TOKEN =
  /(\/(?:v1\/)?(?:sign|embed|qa|invite|reset-password|test-mail\/confirm|tenant-auth\/invitations)\/)[A-Za-z0-9_-]{16,}/g
const SECRET = /\b(?:psk_(?:live|test)|pwhs)_[A-Za-z0-9_-]+/g
const BEARER = /\b(Bearer)\s+[^\s"',]+/gi
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g

export function scrubText(text: string): string {
  return text
    .replace(LINK_TOKEN, '$1[token]')
    .replace(SECRET, '[secret]')
    .replace(BEARER, '$1 [token]')
    .replace(JWT, '[token]')
    .replace(EMAIL, '[email]')
}

/**
 * Scrubs every string inside `value` in place (objects and arrays, however
 * deep; cycles are followed once). For an error event or breadcrumb about to
 * be sent, so nothing in any field (message, stack value, URL, extra data)
 * slips past.
 */
export function scrubInPlace(value: object, seen: WeakSet<object> = new WeakSet()): void {
  if (seen.has(value)) return
  seen.add(value)
  for (const key of Object.keys(value)) {
    const child: string | number | boolean | object | null | undefined = Reflect.get(value, key)
    if (typeof child === 'string') {
      Reflect.set(value, key, scrubText(child))
    } else if (child !== null && typeof child === 'object') {
      scrubInPlace(child, seen)
    }
  }
}
